import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Chip,
  Alert,
  CircularProgress,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

interface Option {
  id: string;
  name: string;
}

interface PreviewSampleRow {
  row: { businessName?: string; farmName?: string; fieldName?: string; productName?: string; productCategoryName?: string; geneStack?: string; rate?: number; unitName?: string };
  suggestions: {
    business?: Option; farm?: Option; field?: Option; product?: Option;
    productCategory?: Option; unit?: Option; operationType?: Option; geneStack?: Option;
  };
  wouldResolve: boolean;
}

interface ColumnMappingPreview {
  totalMatchingRows: number;
  sample: PreviewSampleRow[];
}

interface DistinctValueRow {
  rawText: string;
  rowCount: number;
  suggestion?: Option;
}

// One entry per column GetDistinctValuesAsync supports — see
// api/Services/Import/ImportedRowMatchingService.cs's DistinctValueColumns.
// aliasApiPath/targetIdField describe how to POST a confirmed mapping (reuses
// the same CRUD alias controllers the Business/Farm/Field/Product/Category/
// GeneStack/Region admin screens already use); targetApiPath/searchable
// describe how to populate the "map to" picker.
interface ColumnConfig {
  column: string;
  label: string;
  aliasApiPath: string;
  targetIdField: string;
  targetApiPath: string;
  targetSearchable: boolean;
  needsBusiness: boolean;
}

const COLUMN_CONFIGS: ColumnConfig[] = [
  { column: 'ProductName', label: 'Product', aliasApiPath: '/api/product-aliases', targetIdField: 'productId', targetApiPath: '/api/products', targetSearchable: true, needsBusiness: false },
  { column: 'ProductCategoryName', label: 'Category', aliasApiPath: '/api/product-category-aliases', targetIdField: 'productCategoryId', targetApiPath: '/api/product-categories', targetSearchable: false, needsBusiness: false },
  { column: 'GeneStack', label: 'Gene Stack', aliasApiPath: '/api/gene-stack-aliases', targetIdField: 'geneStackId', targetApiPath: '/api/gene-stacks', targetSearchable: false, needsBusiness: false },
  { column: 'RegionName', label: 'Region', aliasApiPath: '/api/region-aliases', targetIdField: 'regionId', targetApiPath: '/api/regions', targetSearchable: false, needsBusiness: false },
  { column: 'BusinessName', label: 'Business', aliasApiPath: '/api/business-aliases', targetIdField: 'businessId', targetApiPath: '/api/businesses/list', targetSearchable: false, needsBusiness: false },
  { column: 'FarmName', label: 'Farm', aliasApiPath: '/api/farm-aliases', targetIdField: 'spatialFeatureId', targetApiPath: '/api/spatial-features?category=Farm', targetSearchable: false, needsBusiness: true },
  { column: 'FieldName', label: 'Field', aliasApiPath: '/api/field-aliases', targetIdField: 'spatialFeatureId', targetApiPath: '/api/spatial-features?category=Field', targetSearchable: false, needsBusiness: true },
];

interface Props {
  batchId: string;
  onMappingApplied: () => void;
}

// Column Mapping tab — the "map this one distinct raw value to a real
// record, once, and every row sharing that text picks it up" workflow (see
// the alias tables in api/Models/). One row per distinct value rather than
// one row per ImportedRow: mapping "Bollgard 3 / Roundup Ready Flex" to the
// real GeneStack "Bollgard III RRFlex" here resolves every row carrying that
// exact text, instead of paging through them one at a time in the grid.
const ImportColumnValueMapping: React.FC<Props> = ({ batchId, onMappingApplied }) => {
  const auth = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [columnKey, setColumnKey] = useState(COLUMN_CONFIGS[0].column);
  const config = COLUMN_CONFIGS.find((c) => c.column === columnKey)!;

  const [businesses, setBusinesses] = useState<Option[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);

  const [rows, setRows] = useState<DistinctValueRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The "map to" picker's option list per row — keyed by rawText so each
  // row's Autocomplete can carry its own in-progress selection independent
  // of the others. Cleared/refetched whenever the column or (for Farm/Field)
  // the selected Business changes.
  const [targetOptions, setTargetOptions] = useState<Option[]>([]);
  const [pendingSelection, setPendingSelection] = useState<Record<string, Option | null>>({});
  const [savingRawText, setSavingRawText] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState<Set<string>>(new Set());

  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Businesses list — needed up front for the Farm/Field "pick a Business
  // first" step (see FarmAlias/FieldAlias's per-business scoping).
  useEffect(() => {
    (async () => {
      const res = await fetch('/api/businesses/list', { headers: authHeaders() });
      if (res.ok) setBusinesses((await res.json()).map((b: any) => ({ id: b.id, name: b.name })));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDistinctValues = useCallback(async () => {
    if (config.needsBusiness && !selectedBusinessId) {
      setRows([]);
      setTotalCount(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ column: config.column, page: String(page + 1), pageSize: String(pageSize) });
      if (config.needsBusiness && selectedBusinessId) params.set('businessId', selectedBusinessId);
      const res = await fetch(`/api/import-uploads/${batchId}/rows/distinct-values?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load distinct values');
      const data = await res.json();
      setRows(data.items);
      setTotalCount(data.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, config.column, config.needsBusiness, selectedBusinessId, page, pageSize, authHeaders]);

  useEffect(() => {
    setPage(0);
    setPendingSelection({});
    setJustSaved(new Set());
  }, [columnKey, selectedBusinessId]);

  useEffect(() => {
    fetchDistinctValues();
  }, [fetchDistinctValues]);

  // Target ("map to") options — a one-shot fetch for the small fixed lookups
  // (Category/GeneStack/Region/Business/Farm/Field), refetched whenever the
  // column or (Farm/Field) selected Business changes. Product uses its own
  // debounced search instead (see the Autocomplete's onInputChange below),
  // since the product catalog is too large to load in full.
  useEffect(() => {
    if (config.targetSearchable) {
      setTargetOptions([]);
      return;
    }
    if (config.needsBusiness && !selectedBusinessId) {
      setTargetOptions([]);
      return;
    }
    (async () => {
      const separator = config.targetApiPath.includes('?') ? '&' : '?';
      const url = config.needsBusiness
        ? `${config.targetApiPath}${separator}businessId=${selectedBusinessId}`
        : `${config.targetApiPath}${separator}includeArchived=false`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const items = Array.isArray(data) ? data : data.items ?? [];
      setTargetOptions(items.map((d: any) => ({ id: d.id, name: d.name })));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.column, config.targetSearchable, config.needsBusiness, config.targetApiPath, selectedBusinessId]);

  const handleProductSearch = (value: string) => {
    if (!config.targetSearchable) return;
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(async () => {
      const params = new URLSearchParams({ pageSize: '25' });
      if (value) params.set('search', value);
      const res = await fetch(`${config.targetApiPath}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setTargetOptions((data.items || []).map((d: any) => ({ id: d.id, name: d.name })));
    }, 300);
  };

  const [previewRow, setPreviewRow] = useState<DistinctValueRow | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<ColumnMappingPreview | null>(null);

  // Which key in a sample row's `suggestions` this column's mapping
  // overrides — used to highlight the one field that's actually changing in
  // the preview table. Region isn't shown as its own preview column, so it
  // has no highlight target.
  const suggestionKeyForColumn: Record<string, keyof PreviewSampleRow['suggestions'] | undefined> = {
    BusinessName: 'business', FarmName: 'farm', FieldName: 'field', ProductName: 'product',
    ProductCategoryName: 'productCategory', GeneStack: 'geneStack', RegionName: undefined,
  };

  // Step 1: show the admin exactly what the affected records would look
  // like with this mapping applied, before writing anything — the alias
  // itself is only saved if they confirm (see handleConfirmSave).
  const openPreview = async (row: DistinctValueRow) => {
    const target = pendingSelection[row.rawText];
    if (!target) return;
    setPreviewRow(row);
    setPreviewData(null);
    setPreviewLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/import-uploads/${batchId}/rows/preview-value-mapping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ column: config.column, rawText: row.rawText, targetId: target.id }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to preview this mapping');
      }
      setPreviewData(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setPreviewRow(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewRow(null);
    setPreviewData(null);
  };

  // Step 2: the admin has seen the preview and actually wants to save it —
  // only now does the alias get written.
  const handleConfirmSave = async () => {
    const row = previewRow;
    const target = row ? pendingSelection[row.rawText] : null;
    if (!row || !target) return;
    setSavingRawText(row.rawText);
    setError(null);
    try {
      const body: Record<string, string> = { rawText: row.rawText, [config.targetIdField]: target.id };
      if (config.needsBusiness && selectedBusinessId) body.businessId = selectedBusinessId;
      const res = await fetch(config.aliasApiPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save mapping');
      }
      setJustSaved((prev) => new Set(prev).add(row.rawText));
      closePreview();
      // Refresh this page so the row's Suggestion reflects the mapping that
      // was just confirmed — the running "N of M resolved" counter below is
      // derived straight from these rows, so it updates the moment this
      // resolves rather than needing a separate progress endpoint.
      await fetchDistinctValues();
      onMappingApplied();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSavingRawText(null);
    }
  };

  const resolvedRowCountOnPage = rows.filter((r) => r.suggestion).reduce((sum, r) => sum + r.rowCount, 0);
  const totalRowCountOnPage = rows.reduce((sum, r) => sum + r.rowCount, 0);

  return (
    <Box sx={{ p: 2, overflow: 'auto', height: '100%' }}>
      <Typography variant="subtitle1" gutterBottom>
        Map a distinct raw value to a real record once — every unmatched row in this batch carrying that exact text
        picks it up automatically.
      </Typography>

      <ToggleButtonGroup
        value={columnKey}
        exclusive
        size="small"
        onChange={(_, v) => v && setColumnKey(v)}
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        {COLUMN_CONFIGS.map((c) => (
          <ToggleButton key={c.column} value={c.column}>{c.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {config.needsBusiness && (
        <Autocomplete
          sx={{ maxWidth: 320, mb: 2 }}
          size="small"
          options={businesses}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={businesses.find((b) => b.id === selectedBusinessId) || null}
          onChange={(_, v) => setSelectedBusinessId(v?.id || null)}
          renderInput={(params) => <TextField {...params} label="Business" helperText={`${config.label} names are scoped per Business — pick one to map its values`} />}
        />
      )}

      {config.needsBusiness && !selectedBusinessId ? (
        <Alert severity="info">Pick a Business above to see its unmatched {config.label} values.</Alert>
      ) : (
        <>
          {totalRowCountOnPage > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {resolvedRowCountOnPage} of {totalRowCountOnPage} row(s) shown now resolve {config.label} ({totalCount} distinct value(s) total for this column).
            </Typography>
          )}

          {error && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Alert severity="success">No unmatched rows left with a raw {config.label} value — everything's resolved.</Alert>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Raw Text</TableCell>
                    <TableCell align="right">Rows</TableCell>
                    <TableCell>Current Match</TableCell>
                    <TableCell>Map To</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const selection = pendingSelection[row.rawText] ?? (row.suggestion ? { id: row.suggestion.id, name: row.suggestion.name } : null);
                    return (
                      <TableRow key={row.rawText}>
                        <TableCell>{row.rawText}</TableCell>
                        <TableCell align="right">{row.rowCount}</TableCell>
                        <TableCell>
                          {row.suggestion ? (
                            <Chip size="small" color={justSaved.has(row.rawText) ? 'success' : 'default'} label={row.suggestion.name} />
                          ) : (
                            <Chip size="small" label="No match" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Autocomplete
                            size="small"
                            options={targetOptions}
                            getOptionLabel={(o) => o.name}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            filterOptions={config.targetSearchable ? (opts) => opts : undefined}
                            value={selection}
                            onInputChange={config.targetSearchable ? (_, v) => handleProductSearch(v) : undefined}
                            onChange={(_, v) => setPendingSelection((prev) => ({ ...prev, [row.rawText]: v }))}
                            renderInput={(params) => <TextField {...params} variant="standard" placeholder={`Search ${config.label.toLowerCase()}...`} />}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!pendingSelection[row.rawText] || savingRawText === row.rawText}
                            onClick={() => openPreview(row)}
                          >
                            Preview &amp; Save
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100]}
              />
            </>
          )}
        </>
      )}

      <Dialog open={!!previewRow} onClose={closePreview} maxWidth="lg" fullWidth>
        <DialogTitle>
          {previewRow && pendingSelection[previewRow.rawText]
            ? `Preview: "${previewRow.rawText}" → "${pendingSelection[previewRow.rawText]!.name}"`
            : 'Preview mapping'}
        </DialogTitle>
        <DialogContent>
          {previewLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : previewData ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                This mapping affects <strong>{previewData.totalMatchingRows}</strong> row(s) not yet matched. Below is
                what those record(s) will actually contain — the highlighted column is the value this mapping
                replaces; everything else is that row's current best match, unchanged by this mapping.
              </Typography>
              {previewData.sample.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Business</TableCell>
                      <TableCell>Farm</TableCell>
                      <TableCell>Field</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Gene Stack</TableCell>
                      <TableCell>Rate</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell>Outcome</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.sample.map((s, i) => {
                      const highlightKey = suggestionKeyForColumn[config.column];
                      const cell = (key: keyof PreviewSampleRow['suggestions'], fallback?: string) => (
                        <TableCell
                          sx={key === highlightKey ? { fontWeight: 700, color: 'success.main', bgcolor: 'success.50' } : undefined}
                        >
                          {s.suggestions[key]?.name || fallback || '-'}
                        </TableCell>
                      );
                      return (
                        <TableRow key={i}>
                          {cell('business', s.row.businessName)}
                          {cell('farm', s.row.farmName)}
                          {cell('field', s.row.fieldName)}
                          {cell('product', s.row.productName)}
                          {cell('productCategory', s.row.productCategoryName)}
                          {cell('geneStack', s.row.geneStack)}
                          <TableCell>{s.row.rate ?? '-'}</TableCell>
                          <TableCell>{s.suggestions.unit?.name || s.row.unitName || '-'}</TableCell>
                          <TableCell>
                            {s.wouldResolve ? (
                              <Chip size="small" color="success" label="Will create record" />
                            ) : (
                              <Chip size="small" variant="outlined" label="Still needs review" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Cancel</Button>
          <Button
            variant="contained"
            disabled={previewLoading || !previewData || savingRawText === previewRow?.rawText}
            onClick={handleConfirmSave}
          >
            {savingRawText === previewRow?.rawText ? 'Saving...' : `Confirm & Apply to ${previewData?.totalMatchingRows ?? ''} Row(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImportColumnValueMapping;
